import { useRef, useState, useEffect, useCallback, useId } from 'react';

import { css, keyframes } from '@compiled/react';
import { flattenValues } from '@uniformdev/canvas';
import { useIntl } from 'react-intl';

import VideoPauseOverlayIcon from '@atlaskit/icon/core/video-pause-overlay';
import VideoPlayOverlayIcon from '@atlaskit/icon/core/video-play-overlay';
import { media } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import { Button } from '@/libraries/design/components/button/src/button';
import { Image } from '@/libraries/design/components/image/src/image';
import { wacToken, ColumnLayout } from '@/libraries/design/foundations';
import { checkFeatureFlagGate } from '@/utils/feature-flags/check-feature-flag';
import { featureFlags } from '@/utils/feature-flags/feature-flag-manifest';

import { useRovoHeroAnalytics } from './fire-analytics-event';
import UniformRovoHeroGwp10221 from './rovo-hero-gwp10221.tsx';

interface VideoCarouselSlide {
	team: string;
	mobileVideoSrc: string;
	tabletVideoSrc: string;
	desktopVideoSrc: string;
	mobilePoster?: string;
	tabletPoster?: string;
	desktopPoster?: string;
	ariaLabel?: string;
}

export interface UniformRovoHeroProps {
	defaultHeading: string;
	defaultSubheading?: string;
	videoCarousel: VideoCarouselSlide[];
	primaryCtaLabel?: string;
	primaryCtaHref?: string;
	headingTag?: 'h1' | 'h2';
	flattenData?: boolean;
}

// Helper function for scroll event management (extracted to reduce complexity)
const createScrollEventHandlers = (
	handleFirstInteraction: () => void,
	enableScrollEvents: () => void,
	wheelTimeout: { current: ReturnType<typeof setTimeout> | null },
	keyTimeout: { current: ReturnType<typeof setTimeout> | null },
) => {
	const preventDefault = (e: Event) => {
		e.preventDefault();
		handleFirstInteraction();
	};

	const onWheel = (e: WheelEvent) => {
		preventDefault(e);
		if (wheelTimeout.current) clearTimeout(wheelTimeout.current);
		wheelTimeout.current = setTimeout(enableScrollEvents, 20);
	};

	const onKeyDown = (e: KeyboardEvent) => {
		preventDefault(e);
		if (keyTimeout.current) clearTimeout(keyTimeout.current);
		keyTimeout.current = setTimeout(enableScrollEvents, 20);
	};

	const onTouchMove = (e: TouchEvent) => {
		preventDefault(e);
	};

	const onTouchEnd = () => {
		enableScrollEvents();
	};

	return { onWheel, onKeyDown, onTouchMove, onTouchEnd };
};

/* eslint-disable complexity -- Complex interactive video carousel component */
export const UniformRovoHero = ({
	defaultHeading,
	defaultSubheading,
	videoCarousel,
	primaryCtaLabel,
	primaryCtaHref,
	headingTag = 'h1',
	flattenData = true,
}: UniformRovoHeroProps) => {
	const uniqueId = useId();
	const headingId = `rovo-hero-heading-${uniqueId}`;
	const ctaButtonId = `rovo-hero-cta-${uniqueId}`;
	const videoCarouselData = flattenData
		? (flattenValues(
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				videoCarousel as any,
			) as VideoCarouselSlide[])
		: videoCarousel;

	const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);
	const videoContainerRef = useRef<HTMLDivElement | null>(null);

	const { firePlayPauseClicked, fireTeamSelected } = useRovoHeroAnalytics();
	const [current, setCurrent] = useState(-1);
	const currentRef = useRef(current);
	const [playing, setPlaying] = useState(true);
	const [durations, setDurations] = useState<number[]>(Array(videoCarouselData.length).fill(0));

	const [fillBarResetKey, setFillBarResetKey] = useState(0);

	const [viewportWidth, setViewportWidth] = useState(
		typeof window !== 'undefined' ? window.innerWidth : 0,
	);

	useEffect(() => {
		currentRef.current = current;
	}, [current]);

	// Helper function for setting up video durations (using useCallback to make it stable)
	const setupVideoDurations = useCallback(() => {
		videoCarouselData.forEach((_, idx) => {
			const video = videoRefs.current[idx];
			if (!video) return;
			if (video.readyState >= 1) {
				setDurations((durs) => {
					const copy = [...durs];
					copy[idx] = video.duration;
					return copy;
				});
			} else {
				video.onloadedmetadata = () => {
					setDurations((durs) => {
						const copy = [...durs];
						copy[idx] = video.duration;
						return copy;
					});
				};
			}
		});
	}, [videoCarouselData]);

	// Helper to set up scroll blocking and event listeners (using useCallback to reduce complexity)
	const setupScrollBlockingAndEvents = useCallback(
		(
			handleFirstInteraction: () => void,
			wheelTimeout: { current: ReturnType<typeof setTimeout> | null },
			keyTimeout: { current: ReturnType<typeof setTimeout> | null },
		) => {
			const enableScrollEvents = (handlers: ReturnType<typeof createScrollEventHandlers>) => {
				window.removeEventListener('wheel', handlers.onWheel);
				window.removeEventListener('touchmove', handlers.onTouchMove);
				window.removeEventListener('touchend', handlers.onTouchEnd);
				window.removeEventListener('touchcancel', handlers.onTouchEnd);
				window.removeEventListener('keydown', handlers.onKeyDown);
				if (wheelTimeout.current) clearTimeout(wheelTimeout.current);
				if (keyTimeout.current) clearTimeout(keyTimeout.current);
			};

			const handlers = createScrollEventHandlers(
				handleFirstInteraction,
				() => enableScrollEvents(handlers),
				wheelTimeout,
				keyTimeout,
			);

			const handleFirstClick = () => {
				handleFirstInteraction();
				enableScrollEvents(handlers);
			};

			// Block scroll events
			window.addEventListener('wheel', handlers.onWheel, {
				passive: false,
			});
			window.addEventListener('touchmove', handlers.onTouchMove, {
				passive: false,
			});
			window.addEventListener('touchend', handlers.onTouchEnd, {
				passive: false,
			});
			window.addEventListener('touchcancel', handlers.onTouchEnd, {
				passive: false,
			});
			window.addEventListener('keydown', handlers.onKeyDown, {
				passive: false,
			});

			return {
				handleFirstClick,
				enableScrollEvents: () => enableScrollEvents(handlers),
			};
		},
		[],
	);

	// On-load
	useEffect(() => {
		let hasRun = false;
		let observer: IntersectionObserver | null = null;
		let cleanup: (() => void) | undefined;
		const wheelTimeout = {
			current: null as ReturnType<typeof setTimeout> | null,
		};
		const keyTimeout = {
			current: null as ReturnType<typeof setTimeout> | null,
		};

		function runEffectLogic() {
			if (hasRun) return;
			hasRun = true;

			const handleFirstInteraction = () => {
				if (currentRef.current === -1) {
					setCurrent(0);
				}
			};

			const { handleFirstClick, enableScrollEvents } = setupScrollBlockingAndEvents(
				handleFirstInteraction,
				wheelTimeout,
				keyTimeout,
			);

			// Video container click interactions
			const container = videoContainerRef.current;
			if (container) {
				container.addEventListener('pointerdown', handleFirstClick, {
					once: true,
				});
			}

			// Video durations
			setupVideoDurations();

			// Store cleanup function
			cleanup = () => {
				enableScrollEvents();
				if (container) {
					container.removeEventListener('pointerdown', handleFirstInteraction);
					container.removeEventListener('keydown', handleFirstInteraction);
				}
			};
		}

		// Intersection Observer setup
		const videoContainer = videoContainerRef.current;
		observer = new IntersectionObserver(
			(entries) => {
				const entry = entries[0];
				if (entry && entry.isIntersecting) {
					runEffectLogic();
					observer?.disconnect();
				}
			},
			{ threshold: 0.1 },
		);
		if (videoContainer) {
			observer.observe(videoContainer);
		}

		// Cleanup - ensure scroll events are always re-enabled
		return () => {
			if (observer && videoContainer) {
				observer.unobserve(videoContainer);
			}
			if (cleanup) {
				cleanup();
			}
		};
	}, [setupVideoDurations, setupScrollBlockingAndEvents]);

	// Video handlers - using useCallback to reduce complexity
	const handleEnded = useCallback(() => {
		const currVideo = videoRefs.current[current];
		if (currVideo) {
			currVideo.pause();
			currVideo.currentTime = 0;
		}
		setCurrent((prev) => (prev + 1) % videoCarouselData.length);
	}, [current, videoCarouselData.length]);

	const togglePlay = useCallback(() => {
		setPlaying((p) => {
			firePlayPauseClicked(p ? 'pause' : 'play');
			return !p;
		});
	}, [firePlayPauseClicked]);

	const handleTeamClick = useCallback(
		(idx: number) => {
			if (idx === current) return;
			const currVideo = videoRefs.current[current];
			if (currVideo) {
				currVideo.pause();
				currVideo.currentTime = 0;
			}
			const team = videoCarouselData[idx]?.team ?? String(idx);
			fireTeamSelected(team);
			setCurrent(idx);
			setPlaying(true);
		},
		[current, fireTeamSelected, videoCarouselData],
	);

	// Pause/play current video
	useEffect(() => {
		if (current === -1) return;
		const video = videoRefs.current[current];
		if (video) {
			if (playing) {
				if (video.ended) {
					video.currentTime = 0;
				}
				// eslint-disable-next-line @typescript-eslint/no-empty-function
				video.play().catch(() => {});
			} else {
				video.pause();
			}
		}
	}, [playing, current]);

	const currentDuration = durations[current] || 10;

	// Responsive asset type - extracted to reduce complexity
	useEffect(() => {
		const breakpoints = [768, 1024];
		const prevWidth = { current: window.innerWidth };
		let resizeTimeout: number | undefined;

		const crossedBreakpoint = (prev: number, curr: number) =>
			breakpoints.some((bp) => (prev < bp && curr >= bp) || (prev >= bp && curr < bp));

		const handleResizeEnd = () => {
			const currWidth = window.innerWidth;
			if (crossedBreakpoint(prevWidth.current, currWidth)) {
				setFillBarResetKey((k) => k + 1);
			}
			prevWidth.current = currWidth;
		};

		const handleResize = () => {
			setViewportWidth(window.innerWidth);
			if (resizeTimeout !== undefined) clearTimeout(resizeTimeout);
			resizeTimeout = window.setTimeout(handleResizeEnd, 300);
		};

		window.addEventListener('resize', handleResize);
		return () => {
			window.removeEventListener('resize', handleResize);
			if (resizeTimeout !== undefined) clearTimeout(resizeTimeout);
		};
	}, []);

	const assetType = viewportWidth >= 1024 ? 'desktop' : viewportWidth >= 768 ? 'tablet' : 'mobile';

	const intl = useIntl();

	return (
		<section css={sectionCss}>
			<div
				css={[
					desktopCss,
					desktopLeftAssetCss,
					css({
						transform: current !== -1 ? 'translateX(-120px)' : 'translateX(0)',
					}),
				]}
			>
				<Image
					src="https://dam-cdn.atl.orangelogic.com/AssetLink/ih4h04rnm06355wnp2arca3f4gkg0258.webp"
					alt=""
					style={desktopAssetStyle}
				/>
			</div>
			<div
				css={[
					desktopCss,
					desktopRightAssetCss,
					css({
						transform: current !== -1 ? 'translateX(120px)' : 'translateX(0)',
					}),
				]}
			>
				<Image
					src="https://dam-cdn.atl.orangelogic.com/AssetLink/3qji3u3j76e5i63yjsj7pnko2npm30n4.webp"
					alt=""
					style={desktopAssetStyle}
				/>
			</div>
			<div css={containerCss}>
				<ColumnLayout layout="8">
					<div css={headingContainerCss}>
						{defaultSubheading && (
							<div className="body-lg subheading" css={subheadingCss}>
								{defaultSubheading}
							</div>
						)}
						<div css={headingCss}>
							{headingTag === 'h1' ? (
								<h1 id={headingId} css={mainHeadingCss}>
									{defaultHeading}
								</h1>
							) : (
								<h2 id={headingId} css={mainHeadingCss}>
									{defaultHeading}
								</h2>
							)}
						</div>
						{primaryCtaLabel && primaryCtaHref && (
							<div css={ctaContainerCss}>
								<Button
									id={ctaButtonId}
									href={primaryCtaHref}
									css={ctaCss}
									ariaLabelledby={`${ctaButtonId} ${headingId}`}
									dataEventProps={{
										'data-product-key': 'uniform',
										'data-event': 'clicked',
										'data-event-component': 'linkButton',
										'data-event-container': 'heroSection',
									}}
								>
									{primaryCtaLabel}
								</Button>
							</div>
						)}
					</div>
				</ColumnLayout>
				<div css={[videoContainerCss, videoDimensionsCss]} ref={videoContainerRef}>
					<div css={videoCss}>
						{videoCarouselData.map(
							(
								{
									team,
									mobileVideoSrc,
									tabletVideoSrc,
									desktopVideoSrc,
									mobilePoster,
									tabletPoster,
									desktopPoster,
									ariaLabel,
								},
								idx,
							) => (
								<video
									key={team}
									ref={(el) => (videoRefs.current[idx] = el)}
									src={
										assetType === 'desktop'
											? desktopVideoSrc
											: assetType === 'tablet'
												? tabletVideoSrc
												: mobileVideoSrc
									}
									muted={true}
									playsInline={true}
									css={[
										videoDimensionsCss,
										css({
											visibility:
												idx === current || (idx === 0 && current === -1) ? 'visible' : 'hidden',
											height: `${
												idx === current || (idx === 0 && current === -1) ? '100%' : 0
											} !important`,
										}),
									]}
									onEnded={handleEnded}
									autoPlay={idx === current && playing}
									controls={false}
									aria-label={ariaLabel}
									preload="auto"
									poster={
										(assetType === 'desktop'
											? desktopPoster
											: assetType === 'tablet'
												? tabletPoster
												: mobilePoster) ?? undefined
									}
								/>
							),
						)}
					</div>
				</div>
				{current !== -1 && (
					<ColumnLayout layout="12">
						<div css={controlContainerCss}>
							<button
								type="button"
								css={buttonCss}
								onClick={togglePlay}
								aria-label={intl.formatMessage(
									playing
										? {
												id: 'rovo-hero.pause',
												defaultMessage: 'Pause',
											}
										: {
												id: 'rovo-hero.play',
												defaultMessage: 'Play',
											},
								)}
								aria-pressed={playing}
							>
								<div css={buttonCirlceCss}>
									{playing ? (
										<VideoPauseOverlayIcon
											label={intl.formatMessage({
												id: 'rovo-hero.pause',
												defaultMessage: 'Pause',
											})}
										/>
									) : (
										<VideoPlayOverlayIcon
											label={intl.formatMessage({
												id: 'rovo-hero.play',
												defaultMessage: 'Play',
											})}
										/>
									)}
								</div>
							</button>
							<div css={barContainerCss}>
								{videoCarouselData.map(({ team }, idx) => (
									<button
										key={team}
										type="button"
										onClick={() => handleTeamClick(idx)}
										aria-label={intl.formatMessage(
											{
												id: 'rovo-hero.team-button',
												defaultMessage: 'Play {team} video',
											},
											{ team },
										)}
										aria-pressed={idx === current}
										css={[
											barbuttonCss,
											css({
												color:
													idx === current
														? wacToken('color.primary.blue')
														: wacToken('color.neutral.light800'),

												['&:hover']: {
													cursor: idx === current ? 'auto' : 'pointer',
												},
											}),
										]}
									>
										<div
											css={[
												barCss,
												idx === 0 && firstBarCss,
												idx === videoCarouselData.length - 1 && lastBarCss,
											]}
										>
											{idx === current && (
												<div
													key={fillBarResetKey}
													className="fillBar"
													css={[
														fillBarCss,
														css({
															animation: `${fill} ${currentDuration}s linear forwards`,
															animationPlayState: playing ? 'running' : 'paused',
														}),
													]}
												/>
											)}
										</div>
										{team}
									</button>
								))}
							</div>
						</div>
					</ColumnLayout>
				)}
			</div>
			<div css={mobileCss}>
				<Image
					src="https://dam-cdn.atl.orangelogic.com/AssetLink/6ts3bd426400kq6ds07w1208q7a038r0.webp"
					alt=""
					style={imageStyles}
					css={mobileCss}
				/>
			</div>
			<div css={tabletCss}>
				<Image
					src="https://dam-cdn.atl.orangelogic.com/AssetLink/6325jfp76jcjym74os216mopusw7m420.webp"
					alt=""
					style={imageStyles}
				/>
			</div>
		</section>
	);
};
/* eslint-enable complexity */

const UniformRovoHeroWithFeatureGate = (props: UniformRovoHeroProps) => {
	const isGwp10221 = checkFeatureFlagGate(featureFlags.gwp10221RovoProductTourFixScrollAnimation);

	if (isGwp10221) {
		return <UniformRovoHeroGwp10221 {...props} />;
	}

	return <UniformRovoHero {...props} />;
};

export default UniformRovoHeroWithFeatureGate;

const desktopAssetStyle = {
	width: '787px',
};

const desktopLeftAssetCss = css({
	transition: 'transform 600ms ease-in-out',

	position: 'absolute',
	zIndex: 1,
	insetInlineStart: '-465px',

	[media.only.md]: {
		insetInlineStart: '-680px',
	},
});

const desktopRightAssetCss = css({
	transition: 'transform 600ms ease-in-out',

	position: 'absolute',
	zIndex: 1,
	insetInlineEnd: '-465px',

	[media.only.md]: {
		insetInlineEnd: '-680px',
	},
});

const desktopCss = css({
	[media.below.md]: {
		display: 'none',
	},
});

const tabletCss = css({
	[media.above.md]: {
		display: 'none',
	},
	[media.below.sm]: {
		display: 'none',
	},
});

const mobileCss = css({
	[media.above.sm]: {
		display: 'none',
	},
});

const imageStyles = {
	width: '100%',
	display: 'block',
};

const sectionCss = css({
	overflow: 'hidden',
	position: 'relative',
	backgroundColor: wacToken('color.neutral.light100'),
});

const containerCss = css({
	overflowX: 'hidden',
	paddingBlock: token('space.800'),

	[media.below.sm]: {
		paddingBlock: token('space.300'),
	},
});

const subheadingCss = css({
	'&.body-lg.subheading': {
		fontFamily: wacToken('font.charlie.display'),
		lineHeight: '1.5rem',

		[media.only.sm]: {
			fontSize: '1.25rem',
		},

		[media.below.sm]: {
			fontSize: '0.875rem',
		},
	},
});

const headingContainerCss = css({
	textAlign: 'center',
});

const headingCss = css({
	alignItems: 'center',
	marginBlockStart: token('space.200'),
});

const mainHeadingCss = css({
	margin: 0,

	'&:lang(en)': {
		textWrap: 'balance',
	},
});

const ctaContainerCss = css({
	display: 'flex',
	flexWrap: 'wrap',
	gap: token('space.150'),
	justifyContent: 'center',
	marginBlock: token('space.300'),

	[media.below.sm]: {
		marginBlock: token('space.200'),
	},
});

const ctaCss = css({
	textWrap: 'nowrap',

	[media.below.sm]: {
		fontSize: '1rem',
		lineHeight: '1.25rem',
		fontWeight: '500',
	},
});

const videoContainerCss = css({
	position: 'relative',
	insetInlineStart: '50%',
	transform: 'translateX(-50%)',
});

const videoDimensionsCss = css({
	width: '1168px',
	height: '312px',

	[media.only.sm]: {
		width: '1023px',
		height: '324px',
	},

	[media.below.sm]: {
		width: '375px',
		height: '164px',
	},
});

const videoCss = css({
	width: '100%',
	height: '100%',
	display: 'block',
	textAlign: 'center',
	position: 'relative',
	overflow: 'hidden',
});

const fadeIn = keyframes({
	from: {
		opacity: 0,
		height: 0,
	},
	to: {
		opacity: 1,
		height: '100%',
	},
});

const controlContainerCss = css({
	marginBlockStart: token('space.600'),
	marginInline: 'auto',
	width: '100%',
	maxWidth: '768px',
	gap: token('space.150'),
	animation: `${fadeIn} 0.5s ease-in-out`,

	display: 'grid',
	gridTemplateColumns: 'auto min-content',

	[media.below.sm]: {
		marginBlockStart: token('space.150'),
		gridTemplateColumns: 'auto',
		justifyItems: 'end',
	},
});

const barContainerCss = css({
	display: 'flex',
	alignItems: 'flex-start',
	gap: token('space.150'),
	width: '100%',
});

const buttonCss = css({
	height: '44px',
	width: '44px',
	margin: '-6px',

	backgroundColor: 'transparent',
	border: 'none',
	borderRadius: '100%',

	gridRow: '1',
	gridColumn: '2',

	['&:hover']: {
		cursor: 'pointer',
	},

	['&:focus-visible']: {
		outline: `2px solid ${wacToken('color.primary.blue')}`,
		outlineOffset: '2px',
	},

	[media.below.sm]: {
		gridRow: '1',
		gridColumn: '1',
	},
});

const buttonCirlceCss = css({
	height: '32px',
	width: '32px',
	backgroundColor: wacToken('color.neutral.dark1000'),
	border: 'none',
	borderRadius: '100%',
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',

	['&:hover']: {
		cursor: 'pointer',
		backgroundColor: wacToken('color.neutral.dark800'),
	},

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	span: {
		color: `${wacToken('color.neutral.dark300')} !important`,
	},
});

const firstBarCss = css({
	borderRadius: '10px 0 0 10px',
});

const lastBarCss = css({
	borderRadius: '0 10px 10px 0',
});

const barbuttonCss = css({
	background: 'none',
	border: 'none',
	padding: 0,
	width: '100%',
	fontFamily: wacToken('font.charlie.display'),
	fontSize: '1rem',
	fontWeight: 700,
	lineHeight: '1.5rem',
	['&:focus-visible']: {
		outline: `2px solid ${wacToken('color.primary.blue')}`,
		outlineOffset: '2px',
	},
});

const barCss = css({
	height: '8px',
	width: '100%',
	position: 'relative',
	overflow: 'hidden',
	backgroundColor: wacToken('color.neutral.light300'),
	marginBlock: `${token('space.150')} ${token('space.100')}`,
});

const fillBarCss = css({
	height: '100%',
	width: '100%',
	backgroundColor: wacToken('color.primary.blue'),
	position: 'absolute',
	insetInlineStart: 0,
	insetBlockStart: 0,
});

const fill = keyframes({
	from: { width: '0%' },
	to: { width: '100%' },
});
